import { createId } from '@paralleldrive/cuid2'

import {
  Block,
  BlockKind,
  BlockTypes,
  TemplateData,
} from '../../types/blueprint'

const mapChildArrayId = (block, key) => ({
  ...block,
  [key]: block[key]?.map((item, index) => ({
    ...item,
    value: `${block.id}-${index}`,
  })),
})

export const generateBlockIds = (block) => {
  switch (block.type) {
    case 'checkbox':
      return mapChildArrayId(block, 'checkboxes')
    case 'radio':
      return mapChildArrayId(block, 'radioButtons')
    case 'select':
      return mapChildArrayId(block, 'options')
    default:
      return block
  }
}

type IDS = 'TEMPLATE' | 'SECTION' | 'BLOCK_GROUP' | 'BLOCK'
type Params<T> = T extends 'BLOCK'
  ? { type: BlockTypes; kind: BlockKind }
  : null

export const generateId = <T extends IDS>(type: T, params?: Params<T>) => {
  switch (type) {
    case 'TEMPLATE':
      return `template-${createId()}`
    case 'SECTION':
      return `section-${createId()}`
    case 'BLOCK_GROUP':
      return `blockGroup-${createId()}`
    case 'BLOCK':
      return `${params.type}-${params.kind}-block-${createId()}`
  }
}

const shouldRegenerateId = (block: Block) => {
  const idChunks = block.id?.split('-')

  // something fishy about the current id
  if (idChunks?.length !== 4) return true

  // block type or kind has changed, regenerate
  const [type, kind] = idChunks
  if (block.type !== type || block.kind !== kind) return true

  return false
}

const generateTemplateIds = (template: TemplateData): TemplateData => {
  return {
    ...template,
    id: template.id || generateId('TEMPLATE'),
    sections: template?.sections?.map((section) => ({
      ...section,
      id: section.id || generateId('SECTION'),
      blockGroups: section?.blockGroups?.map((blockGroup) => ({
        ...blockGroup,
        id: blockGroup.id || generateId('BLOCK_GROUP'),
        blocks: blockGroup?.blocks?.map((block) => {
          const id = shouldRegenerateId(block)
            ? generateId('BLOCK', { type: block.type, kind: block.kind })
            : block.id

          return generateBlockIds({ ...block, id })
        }),
      })),
    })),
  }
}

export default generateTemplateIds
